import { Disclosure, DisclosureButton } from '@headlessui/react'
import { useContext, Fragment, useRef, useEffect, ReactNode } from 'react'
import { IconChevronUp } from '@tabler/icons-react'

import { cn } from 'utils/classMerge'
import Text from 'components/ui/text'

import { AccordionContext } from '../accordion'

interface AccordionItemProps {
  /** The index of the accordion item, used to identify the item within the Accordion */
  index: number
  /** The title of the accordion item. Can be a string or a ReactNode for more complex content */
  title: string | ReactNode
  /** The content displayed within the accordion item, shown when the item is open */
  children: ReactNode
  /** Optional class name for the accordion item's root element */
  className?: string
  /** Optional class name for the icon */
  icoClass?: string
  /** A function to wrap the button content with custom styling or structure
   * @example
   * ```tsx
   * buttonWrapper={(children) => <div className="custom-wrapper">{children}</div>}
   * ```
   */
  buttonWrapper?: (children: ReactNode) => ReactNode
  /** A function to wrap the content area with custom styling or structure
   * @example
   * ```tsx
   * contentWrapper={(children) => <div className="custom-content">{children}</div>}
   * ```
   */
  contentWrapper?: (children: ReactNode) => ReactNode
  /** Optional callback function that is called when the accordion item's state changes
   * @param openIndexes - Array of currently open accordion indexes
   * @param index - The index of the clicked item
   */
  onClick?: (openIndexes: number[], index: number) => void
  /** Test ID for the accordion item, useful for testing purposes */
  testId?: string
}

const AccordionItem = ({
  index,
  title,
  children,
  className,
  icoClass,
  buttonWrapper = (kids) => kids,
  contentWrapper = (kids) => kids,
  onClick,
  testId = 'accordion-item',
}: AccordionItemProps) => {
  const context = useContext(AccordionContext)
  if (!context)
    throw new Error('AccordionItem must be used within an Accordion')

  const { openIndexes, toggleAccordion, defaultOpenIndexes } = context
  const isOpen = openIndexes.includes(index)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (
      typeof defaultOpenIndexes === 'boolean' &&
      defaultOpenIndexes &&
      !isOpen
    ) {
      toggleAccordion(index)
    }
  }, [])

  useEffect(() => {
    if (!panelRef.current) return
    panelRef.current.style.maxHeight = `${
      isOpen ? panelRef.current.scrollHeight : 0
    }px`
  }, [isOpen])

  const buttonContent = (
    <DisclosureButton
      className={cn(
        'tw-w-full tw-flex tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-p-0 tw-m-0 tw-text-zinc-800',
        className,
      )}
      onClick={() => {
        toggleAccordion(index)
        onClick?.(openIndexes, index)
      }}
    >
      {typeof title === 'string' ? (
        <Text className="tw-order-2 tw-text-base tw-m-0 tw-font-bold tw-text-zinc-800">
          {title}
        </Text>
      ) : (
        title
      )}
      <IconChevronUp
        className={cn(
          icoClass,
          'tw-transition-transform tw-duration-200 tw-rotate-180 order-1',
          {
            'tw-rotate-90': !isOpen,
          },
        )}
      />
    </DisclosureButton>
  )

  const contentContent = (
    <div
      data-testid={`${testId}-${index}`}
      ref={panelRef}
      className="tw-overflow-hidden tw-transition-[max-height] tw-duration-300 tw-ease-in-out tw-max-h-0 tw-p-0 tw-w-full"
    >
      <div>{children}</div>
    </div>
  )

  return (
    <Disclosure as={Fragment} defaultOpen={isOpen}>
      {buttonWrapper(buttonContent)}
      {contentWrapper(contentContent)}
    </Disclosure>
  )
}

export { AccordionItem }
